import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ColumnsPanelTrigger, DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams,
  GridRowSelectionModel, Toolbar, ToolbarButton, gridColumnLookupSelector,
  gridColumnReorderDragColSelector, gridRowGroupingSanitizedModelSelector,
  useGridApiContext, useGridApiRef, useGridSelector,
} from "@mui/x-data-grid-premium";
import { Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, ListItemIcon, ListItemText, Menu, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import MergeIcon from "@mui/icons-material/Merge";
import SyncIcon from "@mui/icons-material/Sync";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterIcon from "assets/icons/filter.svg?react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight as faChevRight, faChevronUp, faXmark } from "@fortawesome/free-solid-svg-icons";
import { axiosInstance } from "api/axios";
import contractorsService from "api/contractorsService";
import registryInvoicesService from "api/registryInvoicesService";
import bankingService from "api/bankingService";
import postingSchemeService, { SCHEMES_INDEX_QUERY_KEY } from "api/postingSchemeService";
import { useQueryClient } from "react-query";
import useCustomQuery from "hooks/useCustomQuery";
import { IContractor, ICreateContractorPayload } from "dto/Contractors/IContractor";
import { IFinancialOperation } from "dto/Banking/IBanking";
import { plGridLocales } from "utils/muiGridLocales";
import Search from "assets/icons/search.gif";
import InfoIcon from "assets/icons/info_icon.svg?react";
import ContractorMetricSlide, { ContractorSlideData } from "components/Registry/ContractorsRegister/ContractorMetricSlide";
import ContractorDetailPanel from "components/Registry/ContractorsRegister/ContractorDetailPanel";
import { ContractorRow } from "components/Registry/ContractorsRegister";
import {
  fetchAllKSeFPages,
  linkTransactionsToRows,
  loadFkAccountLookup,
  lookupFkAcct,
  computeContractorAmounts,
} from "components/Registry/ContractorsRegister/contractorUtils";
import {
  ProposalEntry, PROPOSAL_SOURCE_STYLE, ManualNipInput,
} from "components/Registry/ContractorsRegister/nipProposals";
import styles from "components/Registry/ContractorsRegister/styles.module.scss";
import { FC, Props } from "components/Registry/ContractorsRegister/typings";
import highlightText from "utils/highlightText";

// Kartoteka kontrahentów — reużywa mechanizmów rejestru „Kontrahenci" (siatka, scalanie,
// edycja NIP inline, panel szczegółów, metryka), ale dane bierze z trwałej kartoteki w
// bazie (/contractors), a nie z agregacji faktur/operacji bankowych.

const CONTRACTORS_ROWS_KEY  = "zois_contractors_rows";
const HANDLED_IBANS_KEY     = "zois_handled_ibans";
const LS_CONTRACTOR_FILTERS = "contractors_savedFilters_v1";

// ── Filtr roli / zakresu ──────────────────────────────────────────────────────

type ContractorRole = "wszyscy" | "dostawcy" | "odbiorcy";

const ROLE_OPTIONS: { value: ContractorRole; label: string }[] = [
  { value: "wszyscy",  label: "Wszyscy" },
  { value: "dostawcy", label: "Dostawcy" },
  { value: "odbiorcy", label: "Odbiorcy" },
];

interface ContractorFilters { sources: string[]; }
const EMPTY_CONTRACTOR_FILTERS: ContractorFilters = { sources: [] };

interface ContractorSavedFilter {
  Id: string;
  Name: string;
  role: ContractorRole;
  sources: string[];
}

type PopupPos = { top: number; left: number } | null;

// ── Persystencja obsłużonych propozycji IBAN ─────────────────────────────────
// Każdy IBAN zatwierdzony lub odrzucony trafia do localStorage — nie wraca
// przy kolejnych otwarciach kartoteki, niezależnie od stanu synchronizacji DB.

function loadHandledIbans(): Set<string> {
  try {
    const raw = localStorage.getItem(HANDLED_IBANS_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch { return new Set(); }
}

function markIbanHandled(iban: string): void {
  try {
    const s = loadHandledIbans();
    s.add(iban);
    localStorage.setItem(HANDLED_IBANS_KEY, JSON.stringify([...s]));
  } catch { /* ignore — localStorage pełny */ }
}

function markIbansHandled(ibans: string[]): void {
  try {
    const s = loadHandledIbans();
    ibans.forEach(ib => s.add(ib));
    localStorage.setItem(HANDLED_IBANS_KEY, JSON.stringify([...s]));
  } catch { /* ignore */ }
}

// ── Walidacja PESEL (wagi [1,3,7,9,1,3,7,9,1,3,1]) ─────────────────────────

function validatePesel(p: string): boolean {
  if (!/^\d{11}$/.test(p)) return false;
  const w = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3, 1];
  return p.split("").reduce((s, d, i) => s + parseInt(d) * w[i], 0) % 10 === 0;
}

// ── Normalizacja ─────────────────────────────────────────────────────────────

const normalizeIban = (s: string) => s.replace(/\s/g, "").toUpperCase();

const normalizeName = (s: string) =>
  s.toLowerCase().replace(/\s+/g, " ").trim();

const normalizeNip = (s: string) =>
  (s ?? "").replace(/[\s.-]/g, "").toUpperCase();

// ── Normalizacja odpowiedzi listy ─────────────────────────────────────────────

function asContractorArray(data: unknown): IContractor[] {
  if (Array.isArray(data)) return data as IContractor[];
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    const candidate =
      obj.Items ?? obj.Contractors ?? obj.Data ?? obj.items ?? obj.data;
    if (Array.isArray(candidate)) return candidate as IContractor[];
  }
  return [];
}

// ── Mapowanie rekordu kartoteki (DB) → wiersz siatki ──────────────────────────

function toRow(c: IContractor): ContractorRow {
  return {
    id: c.Id,
    name: c.Name ?? "",
    nip: c.NIP ?? c.PESEL ?? "",
    ibans: c.PrimaryIban ? [normalizeIban(c.PrimaryIban)] : [],
    docCount: 0,
    sources: [],
    invoices: [],
    bankOps: [],
    acctCode: c.AccountingCode || undefined,
  };
}

// ── Wpisy z wczytanego pliku CSV (migracja) → wiersze siatki ───────────────────

function readCsvContractorRows(): ContractorRow[] {
  try {
    const raw = localStorage.getItem(CONTRACTORS_ROWS_KEY);
    if (!raw) return [];
    const { entries } = JSON.parse(raw) as { entries?: { code?: string; name?: string; nip?: string }[] };
    if (!Array.isArray(entries)) return [];
    console.info(`[Kartoteka kontrahentów] wczytano ${entries.length} kontrahentów z pliku migracji`);
    return entries
      .map((e) => ({ code: (e.code ?? "").trim(), name: (e.name ?? "").trim(), nip: normalizeNip(e.nip ?? "") }))
      .filter((e) => e.name)
      .map((e, i) => ({
        id: `csv:${e.nip || e.code || e.name || i}`,
        name: e.name,
        nip: e.nip,
        ibans: [] as string[],
        docCount: 0,
        sources: ["Migracja"],
        invoices: [],
        bankOps: [],
        acctCode: e.code || undefined,
      }));
  } catch (err) {
    console.error("[Kartoteka kontrahentów] błąd odczytu pliku CSV migracji:", err);
    return [];
  }
}

// ── Deduplikacja wierszy (BE + plik CSV) ───────────────────────────────────────

function mergeInto(target: ContractorRow, r: ContractorRow): void {
  for (const s of r.sources) if (!target.sources.includes(s)) target.sources.push(s);
  for (const ib of r.ibans) if (!target.ibans.includes(ib)) target.ibans.push(ib);
  if (!target.nip && r.nip) target.nip = r.nip;
  if (!target.name && r.name) target.name = r.name;
  if (!target.acctCode && r.acctCode) target.acctCode = r.acctCode;
  target.docCount += r.docCount;
  target.invoices = [...target.invoices, ...r.invoices];
  target.bankOps = [...target.bankOps, ...r.bankOps];
}

function dedupeContractorRows(rows: ContractorRow[]): ContractorRow[] {
  const result: ContractorRow[] = [];
  const byNip = new Map<string, ContractorRow>();
  const byIban = new Map<string, ContractorRow>();
  const byName = new Map<string, ContractorRow>();

  for (const raw of rows) {
    const r: ContractorRow = {
      ...raw,
      sources: [...raw.sources],
      ibans: [...raw.ibans],
      invoices: [...raw.invoices],
      bankOps: [...raw.bankOps],
    };
    const nipKey = normalizeNip(r.nip);

    if (nipKey) {
      const t = byNip.get(nipKey);
      if (t) { mergeInto(t, r); for (const ib of t.ibans) byIban.set(ib, t); continue; }
      result.push(r);
      byNip.set(nipKey, r);
      for (const ib of r.ibans) byIban.set(ib, r);
      continue;
    }

    let t: ContractorRow | undefined;
    for (const ib of r.ibans) { t = byIban.get(ib); if (t) break; }
    if (t) { mergeInto(t, r); continue; }

    const nameKey = normalizeName(r.name);
    const tn = nameKey ? byName.get(nameKey) : undefined;
    if (tn) { mergeInto(tn, r); continue; }

    result.push(r);
    for (const ib of r.ibans) byIban.set(ib, r);
    if (nameKey) byName.set(nameKey, r);
  }

  return result.sort((a, b) =>
    a.name.localeCompare(b.name, "pl", { sensitivity: "base" }),
  );
}

// ── Definicja źródeł (do kolumny zbiorczej „Źródła") ─────────────────────────

const SOURCE_DEFS = [
  { abbr: "K",  matches: ["Kartoteka", "Migracja"], color: "#5d49c7", bg: "rgba(93,73,199,0.10)",  label: "Kartoteka / Migracja" },
  { abbr: "S",  matches: ["Sprzedaż"],              color: "#2e7d32", bg: "rgba(46,125,50,0.10)",  label: "Sprzedaż (faktura)" },
  { abbr: "Z",  matches: ["Zakup", "Rejestr"],      color: "#1565c0", bg: "rgba(21,101,192,0.10)", label: "Zakup / Rejestr" },
  { abbr: "W↑", matches: ["Wpływ"],                 color: "#00695c", bg: "rgba(0,105,92,0.10)",   label: "Wpływ bankowy" },
  { abbr: "W↓", matches: ["Wypływ"],                color: "#bf360c", bg: "rgba(191,54,12,0.10)",  label: "Wypływ bankowy" },
];

// ── Formatowanie IBAN do wyświetlenia ─────────────────────────────────────────

function fmtIban(raw: string): string {
  if (!raw) return "";
  const digits = raw.replace(/^PL/i, "").replace(/\D/g, "");
  if (digits.length !== 26) return raw;
  const groups = digits.match(/.{1,4}/g) ?? [];
  return `PL ${groups.join(" ")}`;
}

// ── Nakładka "brak danych" ────────────────────────────────────────────────────

const CustomNoRowsOverlay = () => (
  <div className={styles.empty}>
    <img src={Search} alt="" />
    <div>Kartoteka kontrahentów jest pusta.</div>
  </div>
);

// ── Typy i helpers propozycji IBAN ───────────────────────────────────────────

interface IbanProposal {
  iban: string;
  counterpartyName: string;
  opCount: number;
  totalAmount: number;
  direction: string;
  candidate: ContractorRow | null;
  score: number;
}

function nameSimilarity(a: string, b: string): number {
  const words = (s: string) =>
    s.toLowerCase()
      .replace(/[^\wąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, " ")
      .split(/\s+/)
      .filter(w => w.length >= 3);
  const wa = new Set(words(a));
  const wb = words(b);
  if (!wa.size || !wb.length) return 0;
  const matches = wb.filter(w => wa.has(w)).length;
  return matches / Math.max(wa.size, wb.length);
}

// ── Panel propozycji IBAN (poza komponentem — stała referencja) ───────────────

interface IbanPanelProps {
  proposals: IbanProposal[];
  onApprove: (proposal: IbanProposal) => void;
  onDismiss: (iban: string) => void;
  onDismissAll: () => void;
  onRegisterNew: (iban: string, name: string, nip: string) => Promise<void>;
}

const IbanProposalsPanel = ({ proposals, onApprove, onDismiss, onDismissAll, onRegisterNew }: IbanPanelProps) => {
  const [formIban, setFormIban] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formNip, setFormNip] = useState("");
  const [formSaving, setFormSaving] = useState(false);

  const openForm = (p: IbanProposal) => {
    setFormIban(p.iban);
    setFormName(p.counterpartyName);
    setFormNip("");
  };

  const closeForm = () => { setFormIban(null); setFormName(""); setFormNip(""); };

  const handleSubmit = async () => {
    if (!formIban || !formName.trim()) return;
    setFormSaving(true);
    try {
      await onRegisterNew(formIban, formName.trim(), formNip.replace(/\D/g, ""));
      closeForm();
    } finally {
      setFormSaving(false);
    }
  };

  const formNipDigits = formNip.replace(/\D/g, "");
  const formIsPesel = formNipDigits.length === 11;
  const formPeselValid = formIsPesel && validatePesel(formNipDigits);

  if (!proposals.length) return null;
  return (
    <div style={{
      margin: "8px 16px 0",
      border: "1px solid rgba(93,73,199,0.25)",
      borderRadius: 8,
      background: "#faf9fe",
      overflow: "hidden",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "rgba(93,73,199,0.06)", borderBottom: "1px solid rgba(93,73,199,0.15)" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#5d49c7", flex: 1 }}>
          🔗 {proposals.length === 1 ? "Wykryto 1 nowy rachunek bankowy" : `Wykryto ${proposals.length} nowe rachunki bankowe`} — zweryfikuj powiązania
        </span>
        <button onClick={onDismissAll} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 12, padding: "2px 6px" }}>
          Odrzuć wszystkie
        </button>
      </div>
      {proposals.map(p => (
        <React.Fragment key={p.iban}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderBottom: formIban === p.iban ? "none" : "1px solid rgba(0,0,0,0.05)", fontSize: 12 }}>
            <span style={{ fontFamily: "monospace", color: "#5d49c7", flexShrink: 0 }}>{fmtIban(p.iban)}</span>
            <span style={{ color: "#555", flexShrink: 0, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.counterpartyName}>{p.counterpartyName}</span>
            <span style={{ color: "#aaa", flexShrink: 0 }}>({p.opCount} op., {p.totalAmount.toLocaleString("pl-PL", { style: "currency", currency: "PLN" })})</span>
            <span style={{ flex: 1 }} />
            {p.candidate ? (
              <>
                <span style={{ color: "#333", fontSize: 11 }}>→ <strong>{p.candidate.name}</strong> <span style={{ color: "#888" }}>({Math.round(p.score * 100)}% zgod.)</span></span>
                <Tooltip title={`Powiąż rachunek ${p.iban} z kontrahentem: ${p.candidate.name}`} placement="top">
                  <IconButton size="small" onClick={() => onApprove(p)} sx={{ color: "#2e7d32", p: "3px" }}>
                    <CheckIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              formIban === p.iban ? (
                <span style={{ color: "#5d49c7", fontSize: 11, fontWeight: 500 }}>↓ uzupełnij dane kontrahenta</span>
              ) : (
                <>
                  <span style={{ color: "#bbb", fontSize: 11 }}>brak kandydata</span>
                  <Tooltip title="Zarejestruj nowego kontrahenta z tym rachunkiem" placement="top">
                    <button
                      onClick={() => openForm(p)}
                      style={{ background: "rgba(93,73,199,0.08)", border: "1px solid rgba(93,73,199,0.25)", borderRadius: 5, color: "#5d49c7", fontSize: 11, fontWeight: 600, padding: "2px 8px", cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      + Nowy kontrahent
                    </button>
                  </Tooltip>
                </>
              )
            )}
            <Tooltip title="Odrzuć tę propozycję" placement="top">
              <IconButton size="small" onClick={() => { if (formIban === p.iban) closeForm(); onDismiss(p.iban); }} sx={{ color: "#bbb", p: "3px", "&:hover": { color: "#c62828" } }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </div>

          {formIban === p.iban && (
            <div style={{ padding: "10px 16px 14px", borderBottom: "1px solid rgba(0,0,0,0.06)", background: "rgba(93,73,199,0.03)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: "6px 12px", alignItems: "center", fontSize: 12 }}>
                <label style={{ color: "#555", textAlign: "right", fontWeight: 500 }}>Nazwa <span style={{ color: "#c62828" }}>*</span></label>
                <input
                  autoFocus
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleSubmit(); if (e.key === "Escape") closeForm(); }}
                  placeholder="Nazwa kontrahenta"
                  style={{ border: "1px solid #ccc", borderRadius: 5, padding: "4px 8px", fontSize: 12, outline: "none" }}
                />
                <label style={{ color: "#555", textAlign: "right", fontWeight: 500 }}>NIP / PESEL</label>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    value={formNip}
                    onChange={e => setFormNip(e.target.value.replace(/\D/g, "").slice(0, 11))}
                    onKeyDown={e => { if (e.key === "Enter") handleSubmit(); if (e.key === "Escape") closeForm(); }}
                    placeholder="opcjonalnie"
                    style={{ border: "1px solid #ccc", borderRadius: 5, padding: "4px 8px", fontSize: 12, outline: "none", width: 160 }}
                  />
                  {formIsPesel && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: formPeselValid ? "#2e7d32" : "#c62828" }}
                      title={formPeselValid ? "Suma kontrolna PESEL poprawna" : "Błędna suma kontrolna PESEL"}>
                      {formPeselValid ? "✓ PESEL" : "✗ PESEL"}
                    </span>
                  )}
                </div>
                <label style={{ color: "#555", textAlign: "right", fontWeight: 500 }}>IBAN</label>
                <span style={{ fontFamily: "monospace", fontSize: 11, color: "#5d49c7" }}>{fmtIban(p.iban)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                <button onClick={closeForm} style={{ background: "none", border: "1px solid #ccc", borderRadius: 5, padding: "4px 14px", fontSize: 12, cursor: "pointer", color: "#555" }}>
                  Anuluj
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={formSaving || !formName.trim()}
                  style={{ background: formName.trim() ? "#5d49c7" : "#ccc", border: "none", borderRadius: 5, padding: "4px 16px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: formName.trim() ? "pointer" : "default", opacity: formSaving ? 0.6 : 1 }}
                >
                  {formSaving ? "Zapisuję…" : "Zarejestruj"}
                </button>
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── Typy GUS ──────────────────────────────────────────────────────────────────

type GusStatus = "loading" | "match" | "mismatch" | "notfound" | "error";
interface GusEntry { gusName: string | null; status: GusStatus; }

// ── Toolbar (poza komponentem — stała referencja, bez odmontowywania przy re-renderze) ──

interface ToolbarBag {
  nipSearchMode: boolean;
  noNipCount: number;
  canFinish: boolean;
  selectedSize: number;
  mergeEnabled: boolean;
  isReconciling: boolean;
  isDeduping: boolean;
  isResetting: boolean;
  gusMode: boolean;
  gusVerifying: boolean;
  gusMismatchCount: number;
  showDuplicates: boolean;
  onRefresh: () => void;
  onToggleNipSearch: () => void;
  onFinish: () => void;
  onMerge: () => void;
  onReconcile: () => void;
  onDedupe: () => void;
  onReset: () => void;
  onBackfill: () => void;
  backfilling: boolean;
  onStartGus: () => void;
  onStopGus: () => void;
  onToggleShowDuplicates: () => void;
  // Zakres / rola
  roleFilter: ContractorRole;
  onRoleFilterChange: (v: ContractorRole) => void;
  // Filtry
  filterOpen: boolean;
  onToggleFilterOpen: () => void;
  activeFilterCount: number;
  // Twoje filtry
  savedFiltersPos: PopupPos;
  onSavedFiltersBtnClick: (pos: { top: number; left: number }) => void;
}

const ContractorsToolbar = ({
  nipSearchMode, noNipCount, canFinish,
  selectedSize, mergeEnabled,
  isReconciling, isDeduping,
  gusMode, gusVerifying, gusMismatchCount,
  showDuplicates,
  onToggleNipSearch, onFinish, onMerge,
  onReconcile, onDedupe, onStartGus, onStopGus,
  onBackfill, backfilling,
  onToggleShowDuplicates,
  roleFilter, onRoleFilterChange,
  filterOpen, onToggleFilterOpen, activeFilterCount,
  savedFiltersPos, onSavedFiltersBtnClick,
}: ToolbarBag) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);
  const closeMenu = () => setMenuAnchor(null);

  const gridApiRef = useGridApiContext();
  const rowGroupingModel = useGridSelector(gridApiRef, gridRowGroupingSanitizedModelSelector);
  const columnsLookup    = useGridSelector(gridApiRef, gridColumnLookupSelector);
  const draggedColumn    = useGridSelector(gridApiRef, gridColumnReorderDragColSelector);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (draggedColumn && !rowGroupingModel.includes(draggedColumn) && columnsLookup[draggedColumn]?.groupable !== false)
      gridApiRef.current.addRowGroupingCriteria(draggedColumn);
  };

  const removeGroup = (field: string) => {
    if (columnsLookup[field]?.groupable !== false)
      gridApiRef.current.removeRowGroupingCriteria(field);
  };

  return (
    <Toolbar>
      <div className={styles.mergedToolbar}>

        {/* Sekcja grupowania (lewa) */}
        <div className={styles.groupingSection} onDragOver={handleDragOver}>
          <MoveUpIcon fontSize="small" color="action" sx={{ mr: 0.75, flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
            {rowGroupingModel.length === 0 ? (
              <span style={{ fontSize: 13, color: "#9e9e9e", whiteSpace: "nowrap" }}>
                Przeciągnij kolumnę tutaj aby zgrupować
              </span>
            ) : (
              rowGroupingModel.map((field, idx) => (
                <Box key={field} sx={{ display: "flex", alignItems: "center" }}>
                  {idx > 0 && <ChevronRightIcon fontSize="small" color="action" />}
                  <Chip label={columnsLookup[field]?.headerName ?? field} size="small" onDelete={() => removeGroup(field)} sx={{ cursor: "grab" }} />
                </Box>
              ))
            )}
          </Box>
        </div>

        {/* Elementy po prawej */}
        <div className={styles.toolbarRight}>

          {/* Zakres */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Zakres</InputLabel>
            <Select
              label="Zakres"
              value={roleFilter}
              onChange={(e) => onRoleFilterChange(e.target.value as ContractorRole)}
              MenuProps={{ sx: { zIndex: 1500 } }}
            >
              {ROLE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Twoje filtry */}
          <div
            className={styles.savedFiltersBtn}
            onClick={(e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              onSavedFiltersBtnClick({ top: rect.bottom + 4, left: rect.left });
            }}
          >
            Twoje filtry
            <FontAwesomeIcon icon={savedFiltersPos ? faChevronUp : faChevronDown} />
          </div>

          {/* Filtry */}
          <Tooltip title="Filtry" placement="bottom-end">
            <button
              className={`${styles.filterBtn} ${filterOpen ? styles.filterBtnActive : ""}`}
              onClick={onToggleFilterOpen}
            >
              <FilterIcon />
              {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
            </button>
          </Tooltip>

          {/* Kolumny */}
          <Tooltip title="Kolumny" placement="bottom-end">
            <ColumnsPanelTrigger render={<ToolbarButton sx={{ border: "1px solid rgba(0,0,0,0.23)", borderRadius: "4px", padding: "4px 8px", color: "#5f5c71", minWidth: "unset", "&:hover": { borderColor: "#5d49c7", background: "rgba(93,73,199,0.04)", color: "#5d49c7" } }} />}>
              <ViewColumnIcon fontSize="small" />
            </ColumnsPanelTrigger>
          </Tooltip>

          {/* Separator */}
          <div style={{ width: 1, height: 22, background: "rgba(0,0,0,0.12)", margin: "0 4px" }} />

          {/* Zaznaczono */}
          {selectedSize > 0 && (
            <span className={styles.selectionInfo}>Zaznaczono: {selectedSize}</span>
          )}

          {/* Kebab menu */}
          <Tooltip title="Akcje" placement="bottom">
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => { closeMenu(); onToggleNipSearch(); }}>
            <ListItemIcon>
              <ManageSearchIcon fontSize="small" color={nipSearchMode ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText>
              {nipSearchMode
                ? "Zamknij weryfikację NIP"
                : `Weryfikacja NIP${noNipCount > 0 ? ` (${noNipCount} bez NIP)` : ""}`}
            </ListItemText>
          </MenuItem>

          <MenuItem disabled={!nipSearchMode || !canFinish} onClick={() => { closeMenu(); onFinish(); }}>
            <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Zakończ weryfikację</ListItemText>
          </MenuItem>

          <MenuItem disabled={selectedSize < 2 || !mergeEnabled} onClick={() => { closeMenu(); onMerge(); }}>
            <ListItemIcon><MergeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>
              {selectedSize >= 2 ? `Scal zaznaczone (${selectedSize})` : "Scal zaznaczone"}
            </ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem disabled={isReconciling} onClick={() => { closeMenu(); onReconcile(); }}>
            <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Synchronizuj ze źródłami</ListItemText>
          </MenuItem>

          <MenuItem disabled={backfilling} onClick={() => { closeMenu(); onBackfill(); }}>
            <ListItemIcon><FactCheckIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{backfilling ? "Generuję schematy…" : "Generuj schematy księgowania z dziennika"}</ListItemText>
          </MenuItem>

          <MenuItem disabled={isDeduping} onClick={() => { closeMenu(); onDedupe(); }}>
            <ListItemIcon><CleaningServicesIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Usuń duplikaty</ListItemText>
          </MenuItem>

          {!gusMode ? (
            <MenuItem onClick={() => { closeMenu(); onStartGus(); }}>
              <ListItemIcon><FactCheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Weryfikacja GUS</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={() => { closeMenu(); onStopGus(); }}>
              <ListItemIcon><FactCheckIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText>
                {gusVerifying ? "Sprawdzam GUS…" : `Zakończ GUS (${gusMismatchCount} niezgodności)`}
              </ListItemText>
            </MenuItem>
          )}

          <Divider />

          <MenuItem onClick={() => { closeMenu(); onToggleShowDuplicates(); }}>
            <ListItemIcon>
              {showDuplicates
                ? <CheckIcon fontSize="small" color="primary" />
                : <FilterListIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{showDuplicates ? "Ukryj duplikaty" : "Pokaż duplikaty"}</ListItemText>
          </MenuItem>
        </Menu>
      </div>
    </Toolbar>
  );
};

// ── Komponent główny ──────────────────────────────────────────────────────────

const ContractorsCardRegister: FC<Props> = ({ setSubtitle, searchText = "" }) => {
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState<ContractorRow[]>([]);
  const [metricOf, setMetricOf] = useState<ContractorSlideData | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });
  const [editingNipId, setEditingNipId] = useState<string | null>(null);
  const [nipDraft, setNipDraft] = useState("");
  const [nipSearchMode, setNipSearchMode] = useState(false);
  const [canFinish, setCanFinish] = useState(false);
  const sessionNoNipIds = useRef<Set<string>>(new Set());
  const searchInitiatedRef = useRef<Set<string>>(new Set());
  const [csvTick, setCsvTick] = useState(0);

  const [showDuplicates, setShowDuplicates] = useState(false);
  const { data: contractors, isLoading, refetch } = contractorsService.useList({ includeDuplicates: showDuplicates });
  const { mutateAsync: updateContractor } = contractorsService.useUpdate();
  const { mutateAsync: createContractor } = contractorsService.useCreate();
  const { mutateAsync: fillAccountingCodes } = contractorsService.useFillAccountingCodes();
  const { mutateAsync: mergeContractors } = contractorsService.useMerge();
  const { mutateAsync: undoMerge } = contractorsService.useUndoMerge();
  const { mutateAsync: reconcileInvoices } = contractorsService.useReconcileInvoices();
  const { mutateAsync: reconcileBank } = contractorsService.useReconcileBank();
  const { mutateAsync: reconcileContracts } = contractorsService.useReconcileContracts();
  const { mutateAsync: dedupeContractors } = contractorsService.useDedupe();
  const { mutateAsync: resetContractors } = contractorsService.useReset();

  // ── #1–3: Źródła transakcyjne (te same co ContractorsRegister) ───────────────
  const { data: registryInvoicesData } = registryInvoicesService.useGetInvoices();
  const { data: bankOpsData = [] } = useCustomQuery<IFinancialOperation[]>(
    [bankingService.queryKeys.operations, "", ""],
    async () => (await axiosInstance.get<IFinancialOperation[]>("/banking/operations")).data,
    { retry: 2 },
  );
  const { data: purchaseInvoices = [] } = useCustomQuery<unknown[]>(
    ["ksef.invoices.purchase"],
    () => fetchAllKSeFPages(1),
    { retry: 1, staleTime: 5 * 60 * 1000 },
  );
  const { data: salesInvoices = [] } = useCustomQuery<unknown[]>(
    ["ksef.invoices.sales"],
    () => fetchAllKSeFPages(0),
    { retry: 1, staleTime: 5 * 60 * 1000 },
  );

  const [isReconciling, setIsReconciling] = useState(false);
  const [isDeduping, setIsDeduping] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const [gusMode, setGusMode] = useState(false);
  const [gusVerifying, setGusVerifying] = useState(false);
  const [gusResults, setGusResults] = useState<Map<string, GusEntry>>(new Map());
  const gusGenRef = useRef(0);

  const [ibanProposals, setIbanProposals] = useState<IbanProposal[]>([]);
  const postReconcileRef = useRef(false);
  const hasInitiallyComputedRef = useRef(false);
  const [lastMergeLogId, setLastMergeLogId] = useState<string | null>(null);
  const [dupWarning, setDupWarning] = useState<{
    iban: string; name: string; nip: string; existing: IContractor;
  } | null>(null);

  // ── Filtrowanie (zakres + panel filtrów) ─────────────────────────────────────
  const [roleFilter, setRoleFilter]             = useState<ContractorRole>("wszyscy");
  const [filterOpen, setFilterOpen]             = useState(false);
  const [activeFilters, setActiveFilters]       = useState<ContractorFilters>(EMPTY_CONTRACTOR_FILTERS);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [savedFiltersPos, setSavedFiltersPos]   = useState<PopupPos>(null);
  const [savedFilters, setSavedFilters]         = useState<ContractorSavedFilter[]>([]);
  const [saveFilterName, setSaveFilterName]     = useState("");

  useEffect(() => {
    try { setSavedFilters(JSON.parse(localStorage.getItem(LS_CONTRACTOR_FILTERS) ?? "[]")); } catch { /* */ }
  }, []);

  const persistSavedFilters = (list: ContractorSavedFilter[]) => {
    setSavedFilters(list);
    localStorage.setItem(LS_CONTRACTOR_FILTERS, JSON.stringify(list));
  };

  const activeFilterCount = useMemo(() => activeFilters.sources.length > 0 ? 1 : 0, [activeFilters]);

  const filterOptions = useMemo(() => ({
    sources: [...new Set(rows.flatMap(r => r.sources))].sort(),
  }), [rows]);

  // ── Weryfikacja nazw w GUS ─────────────────────────────────────────────────

  const normForGus = (s: string) => s.toLowerCase().trim().replace(/\s+/g, " ");

  const stopGusMode = useCallback(() => {
    gusGenRef.current++;
    setGusMode(false);
    setGusVerifying(false);
    setGusResults(new Map());
  }, []);

  const triggerGusVerification = useCallback(async () => {
    const gen = ++gusGenRef.current;
    const rowsWithNip = rows.filter(r => /^\d{10}$/.test(r.nip) && !r.id.startsWith("csv:"));

    setGusMode(true);
    setGusVerifying(true);
    setGusResults(new Map(rowsWithNip.map(r => [r.id, { gusName: null, status: "loading" as GusStatus }])));

    for (let i = 0; i < rowsWithNip.length; i++) {
      if (gusGenRef.current !== gen) return;
      if (i > 0) await new Promise(res => setTimeout(res, 350));
      if (gusGenRef.current !== gen) return;

      const row = rowsWithNip[i];
      try {
        const resp = await axiosInstance.get<Record<string, string>>("/bir/search", { params: { nip: row.nip } });
        const gusName = (resp.data?.Name ?? resp.data?.name ?? "").trim();
        if (!gusName) {
          setGusResults(prev => new Map(prev).set(row.id, { gusName: null, status: "notfound" }));
        } else {
          const status: GusStatus = normForGus(row.name) === normForGus(gusName) ? "match" : "mismatch";
          setGusResults(prev => new Map(prev).set(row.id, { gusName, status }));
        }
      } catch (err: unknown) {
        const status: GusStatus = (err as { response?: { status: number } })?.response?.status === 404 ? "notfound" : "error";
        setGusResults(prev => new Map(prev).set(row.id, { gusName: null, status }));
      }
    }

    if (gusGenRef.current === gen) setGusVerifying(false);
  }, [rows]);

  // ── Propozycje IBAN po reconcile ─────────────────────────────────────────────

  const computeIbanProposals = useCallback(() => {
    // Normalizacja do samych cyfr (bez PL i spacji) — unika niezgodności formatu DB ↔ bank
    const stripPl = (s: string) => s.replace(/\s/g, "").toUpperCase().replace(/^PL/, "");
    const knownIbans   = new Set(rows.flatMap(r => r.ibans.map(stripPl)));
    const handledIbans = new Set([...loadHandledIbans()].map(stripPl));
    const unknownMap = new Map<string, { name: string; count: number; amount: number; direction: string }>();
    for (const op of bankOpsData) {
      const ib = stripPl(op.CounterpartyIban ?? "");
      if (!ib || knownIbans.has(ib) || handledIbans.has(ib)) continue;
      const ex = unknownMap.get(ib) ?? { name: op.CounterpartyName ?? "", count: 0, amount: 0, direction: op.Direction };
      ex.count++;
      ex.amount += op.Amount ?? 0;
      unknownMap.set(ib, ex);
    }
    if (!unknownMap.size) { setIbanProposals([]); return; }

    const proposals: IbanProposal[] = [];
    for (const [iban, { name, count, amount, direction }] of unknownMap) {
      let best: ContractorRow | null = null;
      let bestScore = 0;
      for (const row of rows) {
        const s = nameSimilarity(name, row.name);
        if (s > bestScore) { bestScore = s; best = row; }
      }
      proposals.push({ iban, counterpartyName: name, opCount: count, totalAmount: amount, direction, candidate: bestScore >= 0.3 ? best : null, score: bestScore });
    }
    setIbanProposals(proposals.sort((a, b) => b.score - a.score));
  }, [rows, bankOpsData]);

  const handleIbanApprove = useCallback(async (proposal: IbanProposal) => {
    if (!proposal.candidate) return;
    markIbanHandled(proposal.iban);
    setIbanProposals(prev => prev.filter(p => p.iban !== proposal.iban));
    setRows(prev => prev.map(r => r.id === proposal.candidate!.id
      ? { ...r, ibans: [...r.ibans, proposal.iban] }
      : r,
    ));
    try {
      await updateContractor({ id: proposal.candidate.id, payload: { PrimaryIban: proposal.iban } });
    } catch (err) {
      console.error("[IBAN proposal] błąd zapisu:", err);
    }
  }, [updateContractor]);

  // Przy pierwszym załadowaniu widoku — oblicz propozycje IBAN (bezkosztowe, lokalne)
  useEffect(() => {
    if (hasInitiallyComputedRef.current || rows.length === 0) return;
    hasInitiallyComputedRef.current = true;
    computeIbanProposals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, bankOpsData]);

  // Po reconcile (gdy rows są już świeże) — odśwież propozycje IBAN i uruchom GUS
  useEffect(() => {
    if (!postReconcileRef.current || rows.length === 0) return;
    postReconcileRef.current = false;
    computeIbanProposals();
    triggerGusVerification();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const handleDedupe = async () => {
    setIsDeduping(true);
    try {
      const res = await dedupeContractors();
      await refetch();
      alert(res.Merged > 0
        ? `Scalono ${res.Merged} duplikatów (${res.Groups} grup po NIP/IBAN).`
        : "Nie znaleziono duplikatów do scalenia.");
    } catch (err) {
      console.error("[Kartoteka kontrahentów] błąd odgruzowywania:", err);
      alert("Nie udało się usunąć duplikatów. Spróbuj ponownie.");
    } finally {
      setIsDeduping(false);
    }
  };

  // ── Generowanie schematów księgowania z dziennika (backfill) ─────────────────
  const { mutateAsync: backfillSchemes } = postingSchemeService.useBackfill();
  const queryClient = useQueryClient();
  const [backfilling, setBackfilling] = useState(false);
  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await backfillSchemes();
      queryClient.invalidateQueries([SCHEMES_INDEX_QUERY_KEY]);
      alert(res.created > 0
        ? `Wygenerowano ${res.created} schematów księgowania (kontrahentów z kodem konta: ${res.contractors}; pominięto już istniejące: ${res.skipped}).`
        : `Nie wygenerowano nowych schematów (kontrahentów z kodem konta: ${res.contractors}; już istniejących: ${res.skipped}). Upewnij się, że wczytano dziennik księgowań.`);
    } catch (err) {
      console.error("[Kartoteka kontrahentów] błąd generowania schematów:", err);
      alert("Nie udało się wygenerować schematów księgowania.");
    } finally {
      setBackfilling(false);
    }
  };

  // ── Reset i przebudowa kartoteki ─────────────────────────────────────────────
  const handleReset = async () => {
    const confirmed = window.confirm(
      "Wyczyścić całą kartotekę kontrahentów i przebudować ją od nowa?\n\n" +
      "Wszystkie wpisy kontrahentów zostaną usunięte, a następnie odtworzone " +
      "na podstawie faktur, operacji bankowych i umów.\n\n" +
      "Deduplikacja NIE zostanie uruchomiona automatycznie.\n\n" +
      "Tej operacji nie można cofnąć.",
    );
    if (!confirmed) return;

    setIsResetting(true);
    try {
      const res = await resetContractors();
      await Promise.all([reconcileInvoices(), reconcileBank(), reconcileContracts()]);
      await refetch();
      alert(`Kartoteka odbudowana. Usunięto ${res.Removed} starych wpisów.`);
    } catch (err) {
      console.error("[Kartoteka kontrahentów] błąd resetowania:", err);
      alert("Nie udało się zresetować kartoteki. Spróbuj ponownie.");
    } finally {
      setIsResetting(false);
    }
  };

  // ── #6: reconcileContracts dodany do handleReconcile ─────────────────────────
  const handleReconcile = async () => {
    setIsReconciling(true);
    try {
      await Promise.all([reconcileInvoices(), reconcileBank(), reconcileContracts()]);
      await refetch();
      postReconcileRef.current = true; // useEffect uruchomi proposals + GUS gdy rows się odświeżą
    } finally {
      setIsReconciling(false);
    }
  };

  // Jednorazowe uzupełnienie kodów kont dla kontrahentów bez AccountingCode (migracja).
  const hasAutoFilledCodesRef = useRef(false);
  useEffect(() => {
    if (hasAutoFilledCodesRef.current || contractors === undefined) return;
    const list = asContractorArray(contractors);
    if (list.length > 0 && list.some(c => !c.AccountingCode)) {
      hasAutoFilledCodesRef.current = true;
      fillAccountingCodes()
        .then(res => {
          if (res.Filled > 0)
            console.info(`[Kartoteka] uzupełniono kody kont FK dla ${res.Filled} kontrahentów`);
        })
        .catch(err => {
          console.error("[Kartoteka] błąd generowania kodów kont FK:", err);
          hasAutoFilledCodesRef.current = false; // pozwól na ponowną próbę
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractors]);

  // Auto-synchronizacja przy pierwszym otwarciu — gdy kartoteka jest pusta.
  const hasAutoReconciled = useRef(false);
  useEffect(() => {
    if (hasAutoReconciled.current) return;
    if (contractors === undefined) return;
    const dbCount = asContractorArray(contractors).length;
    if (dbCount === 0) {
      hasAutoReconciled.current = true;
      handleReconcile();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractors]);

  // ── Budowanie wierszy: DB + transakcje + FK + CSV ─────────────────────────────
  useEffect(() => {
    const dbRows = asContractorArray(contractors).map(toRow);

    // #1–3: Link transakcji do wierszy DB (faktury + operacje bankowe)
    const enrichedDbRows = linkTransactionsToRows(
      dbRows,
      purchaseInvoices,
      salesInvoices,
      (registryInvoicesData ?? []) as unknown[],
      bankOpsData,
    );

    // #4: Fuzzy match konta FK z ZOiS dla wierszy bez kodu z DB
    const fkLookup = loadFkAccountLookup();
    enrichedDbRows.forEach(row => {
      if (!row.acctCode) row.acctCode = lookupFkAcct(row.name, fkLookup);
    });

    const list = dedupeContractorRows([...enrichedDbRows, ...readCsvContractorRows()]);
    setRows(list);
  }, [contractors, csvTick, purchaseInvoices, salesInvoices, registryInvoicesData, bankOpsData]);

  useEffect(() => {
    const onUpdated = () => setCsvTick((t) => t + 1);
    window.addEventListener("contractors_updated", onUpdated);
    return () => window.removeEventListener("contractors_updated", onUpdated);
  }, []);

  // ── Wyszukiwanie NIP przez apiRef (brak przerenderowań listy) ────────────────

  const checkCompletion = useCallback(() => {
    const ids = [...sessionNoNipIds.current];
    if (ids.length === 0) { setCanFinish(false); return; }
    const allResolved = ids.every(id => {
      const row = apiRef.current.getRow(id) as ContractorRow | null;
      if (!row) return true;
      if (row.proposalLoading) return false;
      if (row.proposals === undefined) return false;
      if (row.nip) return true;
      return row.proposals.length === 0;
    });
    setCanFinish(allResolved);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rejectProposal = useCallback((rowId: string, nip: string) => {
    const currentRow = apiRef.current.getRow(rowId) as ContractorRow | null;
    const updated = (currentRow?.proposals ?? []).filter(p => p.nip !== nip);
    apiRef.current.updateRows([{ id: rowId, proposals: updated }]);
    checkCompletion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkCompletion]);

  const lookupNipForRow = useCallback(async (row: ContractorRow) => {
    const setProposals = (proposals: ProposalEntry[], loading: boolean) =>
      apiRef.current.updateRows([{ id: row.id, proposals, proposalLoading: loading }]);

    if (row.ibans.length > 0) {
      const account = row.ibans[0].replace(/^PL/i, "").replace(/[\s-]/g, "");
      if (account.length === 26) {
        try {
          const date = new Date().toISOString().split("T")[0];
          const res = await fetch(`https://wl-api.mf.gov.pl/api/search/bank-account/${account}?date=${date}`);
          if (res.ok) {
            const data = await res.json();
            const mfHits: ProposalEntry[] = (data?.result?.subjects ?? [])
              .map((s: Record<string, string>) => ({ nip: (s.nip ?? "").replace(/\D/g, ""), name: s.name ?? "", source: "MF" as const }))
              .filter((s: ProposalEntry) => s.nip.length === 10)
              .slice(0, 3);
            if (mfHits.length > 0) { setProposals(mfHits, false); checkCompletion(); return; }
          }
        } catch { /* fallthrough do GUS */ }
      }
    }

    if (row.name.trim().length >= 3) {
      try {
        const res = await axiosInstance.get<Array<Record<string, unknown>>>("/bir/search-by-name", { params: { name: row.name.trim() } });
        const birHits: ProposalEntry[] = (res.data ?? [])
          .map(s => ({ nip: String(s.Nip ?? s.nip ?? "").replace(/\D/g, ""), name: String(s.Name ?? s.name ?? ""), source: "GUS" as const }))
          .filter(s => s.nip.length === 10)
          .slice(0, 5);
        setProposals(birHits, false);
      } catch {
        setProposals([], false);
      }
    } else {
      setProposals([], false);
    }
    checkCompletion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkCompletion]);

  useEffect(() => {
    if (!nipSearchMode) {
      sessionNoNipIds.current = new Set();
      searchInitiatedRef.current = new Set();
      setCanFinish(false);
      return;
    }
    const noNipRows = rows.filter(r => !r.nip);
    sessionNoNipIds.current = new Set(noNipRows.map(r => r.id));
    searchInitiatedRef.current = new Set();

    noNipRows.forEach(r => apiRef.current.updateRows([{ id: r.id, proposals: undefined, proposalLoading: true }]));
    noNipRows.forEach((r, i) => {
      if (searchInitiatedRef.current.has(r.id)) return;
      searchInitiatedRef.current.add(r.id);
      setTimeout(() => lookupNipForRow(r), i * 400);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nipSearchMode]);

  const handleFinish = useCallback(async () => {
    setNipSearchMode(false);
    try {
      const res = await dedupeContractors();
      await refetch();
      alert(res.Merged > 0
        ? `Scalono ${res.Merged} duplikatów (${res.Groups} grup).`
        : "Nie znaleziono duplikatów do scalenia.");
    } catch {
      alert("Odgruzowanie nie powiodło się.");
    }
  }, [dedupeContractors, refetch]);

  // Filtrujemy po !r.nip bezpośrednio z `rows` (nie z refa) — unikamy problemu
  // wyścigu: przy aktywacji trybu useEffect uruchamia się PO renderze i ref
  // jest jeszcze pusty, co powodowało dodanie do gridu niekompletnych wierszy.
  const displayedRows = useMemo(() => {
    let result = nipSearchMode ? rows.filter(r => !r.nip) : rows;

    // Zakres roli
    if (roleFilter === "dostawcy") {
      result = result.filter(r => r.sources.some(s => s === "Zakup" || s === "Wypływ"));
    } else if (roleFilter === "odbiorcy") {
      result = result.filter(r => r.sources.some(s => s === "Sprzedaż" || s === "Wpływ"));
    }

    // Filtry źródeł (OR — wystarczy jedno z zaznaczonych)
    if (activeFilters.sources.length > 0) {
      result = result.filter(r => activeFilters.sources.some(src => r.sources.includes(src)));
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      const qDigits = q.replace(/\D/g, "");
      result = result.filter(r =>
        r.name.toLowerCase().includes(q) ||
        (qDigits.length >= 3 && r.nip.includes(qDigits)) ||
        // IBAN: min. 8 cyfr żeby uniknąć trafień przypadkowych fragmentów
        (qDigits.length >= 8 && r.ibans.some(ib => ib.replace(/\D/g, "").includes(qDigits))) ||
        (r.acctCode?.toLowerCase().includes(q) ?? false),
      );
    }
    return result;
  }, [rows, nipSearchMode, roleFilter, activeFilters, searchText]);

  const noNipCount = useMemo(() => rows.filter(r => !r.nip).length, [rows]);

  const gusMismatchCount = useMemo(() => {
    let n = 0;
    gusResults.forEach(e => { if (e.status === "mismatch") n++; });
    return n;
  }, [gusResults]);

  useEffect(() => {
    const scopeLabel = ROLE_OPTIONS.find(o => o.value === roleFilter)?.label ?? "";

    // Liczba kontrahentów po filtrze zakresu i źródeł, ale przed wyszukiwaniem tekstowym
    let scopeRows = nipSearchMode ? rows.filter(r => !r.nip) : rows;
    if (roleFilter === "dostawcy") {
      scopeRows = scopeRows.filter(r => r.sources.some(s => s === "Zakup" || s === "Wypływ"));
    } else if (roleFilter === "odbiorcy") {
      scopeRows = scopeRows.filter(r => r.sources.some(s => s === "Sprzedaż" || s === "Wpływ"));
    }
    if (activeFilters.sources.length > 0) {
      scopeRows = scopeRows.filter(r => activeFilters.sources.some(s => r.sources.includes(s)));
    }
    const scopeCount = scopeRows.length;

    const prefix = roleFilter !== "wszyscy" ? `${scopeLabel} · ` : "";

    if (!searchText.trim()) {
      setSubtitle(`${prefix}${scopeCount} kontrahentów`);
    } else {
      setSubtitle(`${prefix}${displayedRows.length} z ${scopeCount} kontrahentów`);
    }
  }, [displayedRows.length, rows, nipSearchMode, roleFilter, activeFilters, searchText, setSubtitle]);

  // ── Scalanie ──────────────────────────────────────────────────────────────

  const selectedIds = useMemo(
    () => new Set([...selectionModel.ids].map(String)),
    [selectionModel],
  );

  const mergeEnabled = selectedIds.size >= 2;

  const handleMerge = async () => {
    if (selectedIds.size < 2) return;
    const selected = rows.filter((r) => selectedIds.has(r.id));
    const leader = selected.find((c) => c.nip) ?? selected[0];
    const absorbed = selected.filter((c) => c.id !== leader.id);

    try {
      let lastLogId: string | null = null;
      for (const a of absorbed) {
        const result = await mergeContractors({ CanonicalId: leader.id, DuplicateId: a.id });
        lastLogId = result?.MergeLogId ?? null;
      }
      setLastMergeLogId(lastLogId);
      setSelectionModel({ type: "include", ids: new Set() });
      refetch();
    } catch (err) {
      console.error("[Kartoteka kontrahentów] błąd scalania:", err);
    }
  };

  const handleUndoMerge = async () => {
    if (!lastMergeLogId) return;
    try {
      await undoMerge(lastMergeLogId);
      setLastMergeLogId(null);
      refetch();
    } catch (err) {
      console.error("[Kartoteka kontrahentów] błąd cofania scalenia:", err);
    }
  };

  // ── Edycja NIP inline ────────────────────────────────────────────────────

  const commitNip = useCallback(async (row: ContractorRow, nipOverride?: string) => {
    const nip = (nipOverride ?? nipDraft).replace(/\D/g, "");
    setEditingNipId(null);

    const isNip   = nip.length === 10;
    const isPesel = nip.length === 11;
    const isClear = nip.length === 0;

    if (!isNip && !isPesel && !isClear) return;
    if (nip === row.nip) return;

    if (nipSearchMode) {
      apiRef.current.updateRows([{ id: row.id, nip, proposals: [], proposalLoading: false }]);
      checkCompletion();
    } else {
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, nip } : r));
    }

    const payload = isNip
      ? { NIP: nip, PESEL: "" }
      : isPesel
        ? { PESEL: nip, NIP: "" }
        : { NIP: "", PESEL: "" };

    try {
      await updateContractor({ id: row.id, payload });
    } catch (err) {
      console.error("[Kartoteka kontrahentów] błąd zapisu NIP/PESEL:", err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nipDraft, nipSearchMode, checkCompletion, updateContractor]);

  // ── Detail panel ─────────────────────────────────────────────────────────

  const handleUndoMergeFromPanel = useCallback(async (mergeLogId: string, duplicateName: string) => {
    const confirmed = window.confirm(
      `Rozdzielić rekord „${duplicateName}"?\n\n` +
      "Rekord zostanie przywrócony jako osobny wpis, a powiązane dokumenty i operacje bankowe zostaną przepisane z powrotem.",
    );
    if (!confirmed) return;
    try {
      await undoMerge(mergeLogId);
      setLastMergeLogId(prev => (prev === mergeLogId ? null : prev));
      refetch();
    } catch (err) {
      console.error("[Kartoteka kontrahentów] błąd rozdzielania:", err);
    }
  }, [undoMerge, refetch]);

  const getDetailPanelContent = useCallback(
    (params: GridRowParams) => {
      const row = params.row as ContractorRow;
      return (
        <ContractorDetailPanel
          ibans={row.ibans}
          invoices={row.invoices}
          bankOps={row.bankOps}
          contractorId={row.id}
          onUndoMerge={handleUndoMergeFromPanel}
        />
      );
    },
    [handleUndoMergeFromPanel],
  );

  const getDetailPanelHeight = useCallback(() => "auto" as const, []);

  // ── Rejestracja nowego kontrahenta z propozycji IBAN ─────────────────────────

  const doCreateContractor = useCallback(async (iban: string, name: string, nip: string) => {
    const payload: ICreateContractorPayload = {
      Name: name,
      PrimaryIban: iban,
      ...(nip.length === 10 ? { NIP: nip } : {}),
      ...(nip.length === 11 ? { PESEL: nip } : {}),
    };
    await createContractor(payload);
    markIbanHandled(iban);
    setIbanProposals(prev => prev.filter(p => p.iban !== iban));
    refetch();
  }, [createContractor, refetch]);

  const handleRegisterNew = useCallback(async (iban: string, name: string, nip: string) => {
    if (nip.length === 10 || nip.length === 11) {
      try {
        const params = nip.length === 10 ? { nip } : { pesel: nip };
        const res = await axiosInstance.get<IContractor | null>("/contractors/find", { params });
        if (res.data) {
          setDupWarning({ iban, name, nip, existing: res.data });
          return;
        }
      } catch {
        // brak kontrahenta (404) lub błąd sieci — kontynuuj tworzenie
      }
    }
    await doCreateContractor(iban, name, nip);
  }, [doCreateContractor]);

  const handleDupWarnLink = useCallback(async () => {
    if (!dupWarning) return;
    const { iban, existing } = dupWarning;
    await updateContractor({ id: existing.Id, payload: { PrimaryIban: existing.PrimaryIban ?? iban } });
    markIbanHandled(iban);
    setIbanProposals(prev => prev.filter(p => p.iban !== iban));
    setDupWarning(null);
    refetch();
  }, [dupWarning, updateContractor, refetch]);

  const handleDupWarnForce = useCallback(async () => {
    if (!dupWarning) return;
    const { iban, name, nip } = dupWarning;
    setDupWarning(null);
    await doCreateContractor(iban, name, nip);
  }, [dupWarning, doCreateContractor]);

  // ── Kolumny ───────────────────────────────────────────────────────────────

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "name",
      headerName: "Nazwa kontrahenta",
      flex: 1,
      minWidth: 200,
      renderHeader: () => <div className={styles.header}>Nazwa kontrahenta</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;
        return (
          <div className={styles.nameCell} title={row.name}>
            {row.name
              ? <span dangerouslySetInnerHTML={{ __html: highlightText(row.name, searchText) }} />
              : <span className={styles.noName}>— brak nazwy —</span>
            }
            {row.docCount > 0 && (
              <span className={styles.docCount}>{row.docCount} dok.</span>
            )}
          </div>
        );
      },
    },
    {
      field: "nip",
      headerName: "NIP / PESEL",
      width: 160,
      renderHeader: () => <div className={styles.header}>NIP / PESEL</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;

        if (editingNipId === row.id) {
          const digits = nipDraft.replace(/\D/g, "");
          const isPeselDraft = digits.length === 11;
          const peselValid   = isPeselDraft && validatePesel(digits);
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 2 }} onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                className={styles.nipInput}
                value={nipDraft}
                maxLength={11}
                placeholder="NIP (10) lub PESEL (11)"
                onChange={(e) => setNipDraft(e.target.value.replace(/\D/g, "").slice(0, 11))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitNip(row);
                  if (e.key === "Escape") setEditingNipId(null);
                  e.stopPropagation();
                }}
                onBlur={() => commitNip(row)}
              />
              {isPeselDraft && (
                <span style={{ fontSize: 11, fontWeight: 700, flexShrink: 0, color: peselValid ? "#2e7d32" : "#c62828" }}
                  title={peselValid ? "Suma kontrolna PESEL poprawna" : "Błędna suma kontrolna PESEL"}>
                  {peselValid ? "✓" : "✗"}
                </span>
              )}
              <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => commitNip(row)} sx={{ color: "#2e7d32", p: "2px", minWidth: 0 }}>
                <CheckIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <Tooltip title="Wyczyść NIP / PESEL" placement="top">
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (row.nip && !window.confirm(`Usunąć wartość „${row.nip}" z pola NIP / PESEL?`)) return;
                    commitNip(row, "");
                  }}
                  sx={{ color: "#c62828", p: "2px", minWidth: 0 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </div>
          );
        }

        if (!row.nip) {
          return (
            <span className={styles.editHint} onClick={(e) => { e.stopPropagation(); setEditingNipId(row.id); setNipDraft(""); }}>
              — dodaj NIP / PESEL
            </span>
          );
        }

        const n = row.nip;
        const isPesel = /^\d{11}$/.test(n);
        const isNip   = /^\d{10}$/.test(n);

        if (isPesel) {
          const valid = validatePesel(n);
          return (
            <Tooltip title={`PESEL — kliknij, aby edytować • suma kontrolna: ${valid ? "✓ poprawna" : "✗ błędna"}`} placement="top">
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}
                onClick={(e) => { e.stopPropagation(); setEditingNipId(row.id); setNipDraft(n); }}>
                <span className={styles.nipValue}
                  dangerouslySetInnerHTML={{ __html: highlightText(n, searchText) }} />
                <span style={{ fontSize: 10, fontWeight: 600, color: valid ? "#2e7d32" : "#c62828",
                  background: valid ? "#e8f5e9" : "#ffebee", padding: "1px 4px", borderRadius: 3, flexShrink: 0 }}>
                  PESEL
                </span>
              </span>
            </Tooltip>
          );
        }

        const nipFormatted = isNip
          ? `${n.slice(0,3)}-${n.slice(3,6)}-${n.slice(6,8)}-${n.slice(8)}`
          : n;
        return (
          <Tooltip title="NIP — kliknij, aby edytować" placement="top">
            <span className={styles.nipValue} onClick={(e) => { e.stopPropagation(); setEditingNipId(row.id); setNipDraft(n); }}
              dangerouslySetInnerHTML={{ __html: highlightText(nipFormatted, searchText) }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "__nipLookup",
      headerName: "Propozycja NIP",
      width: 380,
      sortable: false, filterable: false, hideable: false,
      groupable: false, disableColumnMenu: true, disableReorder: true,
      renderHeader: () => <div className={styles.header}>Propozycja NIP (MF · GUS)</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;

        if (row.nip && row.proposals !== undefined && row.proposals.length === 0) {
          return <span style={{ color: "#2e7d32", fontSize: 11, fontStyle: "italic" }}>✓ zaakceptowano</span>;
        }

        if (row.proposalLoading || row.proposals === undefined) {
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0" }}>
              <span className={styles.noData} style={{ fontStyle: "italic" }}>Szukam…</span>
              <ManualNipInput onAccept={(nip) => commitNip(row, nip)} />
            </div>
          );
        }

        const fmtNip = (n: string) => `${n.slice(0,3)}-${n.slice(3,6)}-${n.slice(6,8)}-${n.slice(8)}`;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0" }}>
            {row.proposals.length === 0 && <span className={styles.noData}>Brak wyników automatycznych</span>}
            {row.proposals.map(p => {
              const { color, bg } = PROPOSAL_SOURCE_STYLE[p.source];
              return (
                <div key={p.nip} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: bg, borderRadius: 10, padding: "2px 8px 2px 6px" }}>
                  <span style={{ color, fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" }}>
                    [{p.source}] {p.name} · {fmtNip(p.nip)}
                  </span>
                  <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); commitNip(row, p.nip); }} sx={{ color: "#2e7d32", p: "2px", minWidth: 0 }}>
                    <CheckIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); rejectProposal(row.id, p.nip); }} sx={{ color: "#c62828", p: "2px", minWidth: 0 }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              );
            })}
            <ManualNipInput onAccept={(nip) => commitNip(row, nip)} />
          </div>
        );
      },
    },
    {
      field: "ibans",
      headerName: "Nr rachunku (IBAN)",
      width: 300,
      renderHeader: () => <div className={styles.header}>Nr rachunku (IBAN)</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;
        const ibans = row.ibans ?? [];
        if (!ibans.length) return <span className={styles.noData}>—</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontFamily: "monospace", fontSize: 11 }}
              dangerouslySetInnerHTML={{ __html: highlightText(fmtIban(ibans[0]), searchText) }}
            />
            {ibans.length > 1 && (
              <Tooltip title={<div style={{ fontSize: 11, lineHeight: 1.8 }}>{ibans.map(fmtIban).join("\n")}</div>} placement="top">
                <span className={styles.ibanCountBadge}>+{ibans.length - 1}</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "acct201",
      headerName: "201",
      width: 96,
      sortable: true,
      filterable: false,
      groupable: false,
      renderHeader: () => (
        <Tooltip title="Konto rozrachunkowe z odbiorcami (201)" placement="top">
          <div className={styles.header}>201</div>
        </Tooltip>
      ),
      valueGetter: (_: unknown, row: unknown) => (row as ContractorRow).acctCode ?? "",
      renderCell: (params: GridRenderCellParams) => {
        const code = (params.row as ContractorRow).acctCode;
        if (!code) return <span className={styles.noData}>—</span>;
        const escalated = code.length > 5 && !code.startsWith("X");
        const cell = (
          <span style={{
            fontFamily: "monospace", fontSize: 12,
            color: escalated ? "#92400e" : "#1a1630",
            background: escalated ? "#fef3c7" : "transparent",
            borderRadius: 4, padding: escalated ? "1px 5px" : undefined,
          }}>
            201-{code}
          </span>
        );
        return escalated
          ? <Tooltip title="Kod eskalowany — możliwy duplikat kontrahenta z tym samym NIP/PESEL" placement="top">{cell}</Tooltip>
          : cell;
      },
    },
    {
      field: "acct211",
      headerName: "211",
      width: 96,
      sortable: true,
      filterable: false,
      groupable: false,
      renderHeader: () => (
        <Tooltip title="Konto rozrachunkowe z dostawcami (211)" placement="top">
          <div className={styles.header}>211</div>
        </Tooltip>
      ),
      valueGetter: (_: unknown, row: unknown) => (row as ContractorRow).acctCode ?? "",
      renderCell: (params: GridRenderCellParams) => {
        const code = (params.row as ContractorRow).acctCode;
        if (!code) return <span className={styles.noData}>—</span>;
        const escalated = code.length > 5 && !code.startsWith("X");
        const cell = (
          <span style={{
            fontFamily: "monospace", fontSize: 12,
            color: escalated ? "#92400e" : "#1a1630",
            background: escalated ? "#fef3c7" : "transparent",
            borderRadius: 4, padding: escalated ? "1px 5px" : undefined,
          }}>
            211-{code}
          </span>
        );
        return escalated
          ? <Tooltip title="Kod eskalowany — możliwy duplikat kontrahenta z tym samym NIP/PESEL" placement="top">{cell}</Tooltip>
          : cell;
      },
    },
    {
      field: "sources",
      headerName: "Źródła",
      width: 110,
      sortable: false,
      filterable: false,
      groupable: false,
      renderHeader: () => <div className={styles.header}>Źródła</div>,
      valueGetter: (_: unknown, row: unknown) =>
        ((row as ContractorRow).sources ?? []).join(","),
      renderCell: (params: GridRenderCellParams) => {
        const srcs: string[] = (params.row as ContractorRow).sources ?? [];
        const present = SOURCE_DEFS.filter(d => d.matches.some(m => srcs.includes(m)));
        if (!present.length) return <span className={styles.noData}>—</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            {present.map(d => (
              <Tooltip key={d.abbr} title={d.label} placement="top">
                <span style={{
                  fontFamily: "monospace", fontSize: 10, fontWeight: 700,
                  color: d.color, background: d.bg,
                  borderRadius: 4, padding: "1px 5px",
                  border: `1px solid ${d.color}30`,
                  whiteSpace: "nowrap",
                }}>
                  {d.abbr}
                </span>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      field: "__gusCheck",
      headerName: "Nazwa w GUS",
      width: 300,
      sortable: false, filterable: false, hideable: false,
      groupable: false, disableColumnMenu: true, disableReorder: true,
      renderHeader: () => <div className={styles.header}>Nazwa w GUS</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;
        if (!row.nip) return <span className={styles.noData}>—</span>;
        const entry = gusResults.get(row.id);
        if (!entry) return null;

        if (entry.status === "loading") {
          return <span className={styles.noData} style={{ fontStyle: "italic" }}>Sprawdzam…</span>;
        }
        if (entry.status === "match") {
          return <span style={{ color: "#2e7d32", fontSize: 12 }}>✓ zgodna</span>;
        }
        if (entry.status === "notfound") {
          return <span className={styles.noData}>Nie znaleziono w GUS</span>;
        }
        if (entry.status === "error") {
          return <span style={{ color: "#c62828", fontSize: 12 }}>Błąd zapytania</span>;
        }
        // mismatch
        const gusName = entry.gusName!;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <span style={{ fontSize: 12, color: "#1a1630", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={gusName}>
              {gusName}
            </span>
            <Tooltip title={`Nadpisz nazwę na: ${gusName}`} placement="top">
              <IconButton
                size="small"
                onMouseDown={e => e.preventDefault()}
                onClick={async () => {
                  setRows(prev => prev.map(r => r.id === row.id ? { ...r, name: gusName } : r));
                  setGusResults(prev => new Map(prev).set(row.id, { gusName, status: "match" }));
                  try {
                    await updateContractor({ id: row.id, payload: { Name: gusName } });
                  } catch (err) {
                    console.error("[GUS] błąd zapisu nazwy:", err);
                  }
                }}
                sx={{ color: "#5d49c7", p: "2px", minWidth: 0, flexShrink: 0 }}
              >
                <CheckIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
    {
      field: "__metric",
      headerName: "",
      width: 40, minWidth: 40, maxWidth: 40,
      sortable: false, filterable: false, hideable: false,
      groupable: false, disableColumnMenu: true, disableReorder: true,
      renderHeader: () => null,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ContractorRow;
        return (
          <Tooltip title="Metryka kontrahenta" placement="left">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                // #5: wyliczone obroty Wn/Ma/saldo z powiązanych transakcji
                const { kwotaWn, kwotaMa, saldo } = computeContractorAmounts(row);
                setMetricOf({
                  id: row.id,
                  acctCode: row.acctCode ?? (row.nip || row.ibans[0] || row.id),
                  name: row.name,
                  nip: row.nip,
                  bankAccount: row.ibans[0] ?? "",
                  docCount: row.docCount,
                  kwotaWn, kwotaMa, saldo,
                  invoices: row.invoices,
                  bankOps: row.bankOps,
                });
              }}
              sx={{ color: "#8a7fa5", "&:hover": { color: "#5d49c7", background: "rgba(93,73,199,0.08)" } }}
            >
              <InfoIcon style={{ width: 16, height: 16 }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ], [editingNipId, nipDraft, commitNip, rejectProposal, gusResults, updateContractor, searchText]);

  return (
    <div className={styles.wrapper}>
      <IbanProposalsPanel
        proposals={ibanProposals}
        onApprove={handleIbanApprove}
        onDismiss={iban => { markIbanHandled(iban); setIbanProposals(prev => prev.filter(p => p.iban !== iban)); }}
        onDismissAll={() => { markIbansHandled(ibanProposals.map(p => p.iban)); setIbanProposals([]); }}
        onRegisterNew={handleRegisterNew}
      />

      {lastMergeLogId && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          margin: "8px 16px 0",
          padding: "8px 14px",
          background: "#fff8e1",
          border: "1px solid #ffe082",
          borderRadius: 8,
          fontSize: 13,
        }}>
          <span style={{ flex: 1, color: "#5f4b00" }}>
            Scalenie zostało wykonane. Jeśli popełniono błąd, możesz je cofnąć.
          </span>
          <Tooltip title="Cofnij ostatnie scalenie — przywraca oba rekordy do stanu sprzed operacji" placement="top">
            <button
              onClick={handleUndoMerge}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                height: 30, padding: "0 12px",
                borderRadius: 6, border: "1px solid #f9a825",
                background: "#fff", color: "#e65100",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ↩ Cofnij scalenie
            </button>
          </Tooltip>
          <button
            onClick={() => setLastMergeLogId(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#bbb", padding: "0 2px", fontSize: 16 }}
            title="Zamknij"
          >
            ×
          </button>
        </div>
      )}

      <div className={styles.list}>
        <DataGridPremium
          apiRef={apiRef}
          localeText={plGridLocales}
          slots={{ toolbar: ContractorsToolbar as React.ComponentType, noRowsOverlay: CustomNoRowsOverlay }}
          slotProps={{
            toolbar: {
              nipSearchMode, noNipCount, canFinish,
              selectedSize: selectedIds.size, mergeEnabled,
              isReconciling, isDeduping, isResetting,
              gusMode, gusVerifying, gusMismatchCount,
              showDuplicates,
              onRefresh: refetch,
              onToggleNipSearch: () => setNipSearchMode(m => !m),
              onFinish: handleFinish,
              onMerge: handleMerge,
              onReconcile: handleReconcile,
              onDedupe: handleDedupe,
              onReset: handleReset,
              onBackfill: handleBackfill,
              backfilling,
              onStartGus: triggerGusVerification,
              onStopGus: stopGusMode,
              onToggleShowDuplicates: () => setShowDuplicates(v => !v),
              roleFilter,
              onRoleFilterChange: setRoleFilter,
              filterOpen,
              onToggleFilterOpen: () => setFilterOpen(v => !v),
              activeFilterCount,
              savedFiltersPos,
              onSavedFiltersBtnClick: (pos: { top: number; left: number }) =>
                setSavedFiltersPos(prev => prev ? null : pos),
            } as Record<string, unknown>,
          }}
          showToolbar
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          columnHeaderHeight={30}
          columns={columns}
          columnVisibilityModel={{ __nipLookup: nipSearchMode, __gusCheck: gusMode }}
          getRowHeight={() => nipSearchMode ? "auto" as const : null}
          rows={displayedRows}
          loading={isLoading}
          hideFooter
          disableColumnFilter
          disablePivoting
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": { outline: "none !important" },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": { outline: "none !important" },
            "& .MuiDataGrid-detailPanel": { overflow: "visible" },
          }}
        />
      </div>

      {metricOf && createPortal(
        <ContractorMetricSlide
          contractor={metricOf}
          wpkLookup={new Map()}
          onClose={() => setMetricOf(null)}
        />,
        document.body,
      )}

      {/* Popup: Twoje filtry */}
      {savedFiltersPos && createPortal(
        <>
          <div className={styles.popupBackdrop} onClick={() => setSavedFiltersPos(null)} />
          <div
            className={styles.savedFiltersDropdown}
            style={{ position: "fixed", top: savedFiltersPos.top, left: savedFiltersPos.left, zIndex: 1400 }}
          >
            {savedFilters.length === 0 && (
              <div className={styles.savedFiltersEmpty}>Brak zapisanych filtrów</div>
            )}
            {savedFilters.map((f) => (
              <div
                key={f.Id}
                className={styles.savedFilterItem}
                onClick={() => {
                  setRoleFilter(f.role);
                  setActiveFilters({ sources: f.sources });
                  setSavedFiltersPos(null);
                }}
              >
                <span className={styles.savedFilterName}>{f.Name}</span>
                <button
                  className={styles.savedFilterDel}
                  onClick={(e) => { e.stopPropagation(); persistSavedFilters(savedFilters.filter(x => x.Id !== f.Id)); }}
                >✕</button>
              </div>
            ))}
            <div className={styles.savedFilterSaveRow}>
              <input
                className={styles.savedFilterSaveInput}
                placeholder="Nazwa filtru…"
                value={saveFilterName}
                onChange={e => setSaveFilterName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && saveFilterName.trim()) {
                    persistSavedFilters([...savedFilters, { Id: Date.now().toString(), Name: saveFilterName.trim(), role: roleFilter, sources: activeFilters.sources }]);
                    setSaveFilterName("");
                  }
                }}
              />
              <button
                className={styles.savedFilterSaveBtn}
                disabled={!saveFilterName.trim()}
                onClick={() => {
                  persistSavedFilters([...savedFilters, { Id: Date.now().toString(), Name: saveFilterName.trim(), role: roleFilter, sources: activeFilters.sources }]);
                  setSaveFilterName("");
                }}
              >Zapisz</button>
            </div>
          </div>
        </>,
        document.body,
      )}

      {/* Panel filtru (slide z prawej) */}
      {filterOpen && createPortal(
        <div className={styles.filterOverlay} onClick={() => setFilterOpen(false)}>
          <div className={styles.filterPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.filterPanelHeader}>
              <span className={styles.filterPanelTitle}>Filtry</span>
              <div className={styles.filterPanelActions}>
                {activeFilterCount > 0 && (
                  <button className={styles.filterResetBtn} onClick={() => setActiveFilters(EMPTY_CONTRACTOR_FILTERS)}>
                    Wyczyść
                  </button>
                )}
                <button className={styles.filterPanelCloseBtn} onClick={() => setFilterOpen(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>
            <div className={styles.filterPanelBody}>
              <div className={styles.filterSection}>
                <button
                  className={styles.filterSectionHeader}
                  onClick={() => setCollapsedSections(prev => ({ ...prev, sources: !prev.sources }))}
                >
                  <span className={styles.filterLabel}>
                    Źródło
                    {activeFilters.sources.length > 0 && (
                      <span className={styles.filterSectionBadge}>{activeFilters.sources.length}</span>
                    )}
                  </span>
                  <FontAwesomeIcon
                    icon={collapsedSections.sources ? faChevRight : faChevronDown}
                    className={styles.filterChevron}
                  />
                </button>
                {!collapsedSections.sources && (
                  <>
                    {activeFilters.sources.length > 0 && (
                      <div className={styles.filterChipGroup}>
                        {activeFilters.sources.map((src) => (
                          <Chip
                            key={src}
                            label={src}
                            size="small"
                            onDelete={() => setActiveFilters(prev => ({ ...prev, sources: prev.sources.filter(s => s !== src) }))}
                            sx={{ background: "#5d49c7", color: "#fff", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" } } }}
                          />
                        ))}
                      </div>
                    )}
                    <Autocomplete
                      multiple
                      size="small"
                      options={filterOptions.sources.filter(o => !activeFilters.sources.includes(o))}
                      value={activeFilters.sources}
                      onChange={(_, newVal) => setActiveFilters(prev => ({ ...prev, sources: newVal }))}
                      disableCloseOnSelect
                      renderTags={() => null}
                      renderOption={(props, opt) => {
                        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                        return <li key={key} {...rest} style={{ fontSize: 13 }}>{opt}</li>;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Szukaj źródła…"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 13 }, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#5d49c7" } }}
                        />
                      )}
                      noOptionsText="Brak wyników"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <Dialog open={!!dupWarning} onClose={() => setDupWarning(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          Kontrahent już istnieje w kartotece
        </DialogTitle>
        <DialogContent>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
            Kontrahent z {dupWarning?.nip.length === 10 ? "NIP" : "PESEL"}{" "}
            <strong>{dupWarning?.nip}</strong> jest już zarejestrowany jako:
          </p>
          <p style={{ margin: "8px 0 0", fontSize: 14, fontWeight: 600 }}>
            {dupWarning?.existing.Name}
          </p>
          <p style={{ margin: "12px 0 0", fontSize: 13, color: "#555" }}>
            Czy chcesz powiązać nowy rachunek bankowy z istniejącym kontrahentem,
            czy mimo to utworzyć drugi wpis (kod konta zostanie eskalowany)?
          </p>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDupWarning(null)} size="small" color="inherit">
            Anuluj
          </Button>
          <Button onClick={handleDupWarnForce} size="small" color="warning" variant="outlined">
            Utwórz nowy mimo to
          </Button>
          <Button onClick={handleDupWarnLink} size="small" variant="contained" disableElevation>
            Powiąż z istniejącym
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContractorsCardRegister;
